import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchBookingMessages } from "../../../../../APIs/Messages/GetMessages";
import { useDeviceContext } from "../../../../../context/DeviceContext";
import { useAppContext } from "../../../../../hooks/useAppContext";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { Messages } from "../../../../../types/messages";
import Loading from "../../../../../utils/Loading";
import { Button } from "../../../../Shared/Buttons/Button";
import SendEmail from "./Email/SendEmail";
import { getIsSendDisabled } from "./Email/SendEmailController";
import ViewEmailModal from "./Email/ViewEmailModal";
import MessageRow from "./MessageRow";
import SendSms from "./SMS/SendSms";
import ViewSMS from "./SMS/ViewSmsModal";

const SelectedBookingMessages = () => {
  const [selectedSms, setSelectedSms] = useState<Messages>();
  const [selectedEmail, setSelectedEmail] = useState<Messages>();
  const [showSendSms, setShowSendSms] = useState<boolean>(false);
  const [showSendEmail, setShowSendEmail] = useState<boolean>(false);
  const [messageCount, setMessageCount] = useState<number>(-1);
  const { selectedBookingV4, selectedBooking, venueConfig } = useDiaryContext();
  const { connectionStatus } = useDeviceContext();
  const { venueGroupConfig } = useAppContext();
  const scrollRef = useRef<HTMLDivElement>(null);
  const retryCount = useRef<number>(0);
  const sendEmailDisabled = getIsSendDisabled({
    venueGroupConfig,
    venueConfig,
    selectedBooking
  });

  const bookingId = selectedBooking?.id;
  const {
    data: messages,
    isLoading,
    refetch
  } = useQuery({
    queryKey: ["GetBookingMessages", bookingId],
    queryFn: () => fetchBookingMessages(bookingId)
  });

  const loading = isLoading && connectionStatus === "internet";

  const handleOnMessageRowClick = (message: Messages) => {
    if (message.type === "sms") {
      setSelectedSms(message);
    }

    if (message.type === "email") {
      setSelectedEmail(message);
    }
  };

  useEffect(() => {
    if (messageCount === -1) {
      return;
    }
    if (messageCount === (messages ?? []).length && retryCount.current < 3) {
      // newly added SMS not returned yet, wait and try again.
      retryCount.current++;
      setTimeout(refetch, 1000);
    } else {
      retryCount.current = 0;
      setMessageCount(-1);
    }
  }, [messageCount]);

  return (
    <div className="BookingMessages flex flex-col h-full p-panel-padding">
      {selectedSms && (
        <ViewSMS
          message={selectedSms}
          handleClose={() => setSelectedSms(undefined)}
        />
      )}
      {showSendSms && (
        <SendSms
          handleClose={() => setShowSendSms(false)}
          handleSent={() => {
            // Set current message count instead of just try reload, this is because there is sometimes a delay in the new message being returned.
            setMessageCount((messages ?? []).length);
            setShowSendSms(false);
          }}
        />
      )}
      {selectedEmail && (
        <ViewEmailModal
          message={selectedEmail}
          handleClose={() => setSelectedEmail(undefined)}
        />
      )}
      {showSendEmail && (
        <SendEmail
          handleClose={() => setShowSendEmail(false)}
          handleSent={() => {
            // Set current message count instead of just try reload, this is because there is sometimes a delay in the new message being returned.
            setMessageCount((messages ?? []).length);
            setShowSendEmail(false);
          }}
        />
      )}
      <div className="BookingMessages-Header flex justify-between">
        <Button
          type="default"
          disabled={
            !selectedBookingV4?.phone || !venueConfig?.booking_sms_enabled
          }
          onClick={() => {
            setShowSendSms(true);
          }}
        >
          Send SMS
        </Button>
        <Button
          disabled={sendEmailDisabled}
          type="default"
          onClick={() => {
            setShowSendEmail(true);
          }}
        >
          Compose Email
        </Button>
      </div>
      <div className="BookingMessages-BackgroundContainer flex flex-[1_1_100%]">
        <div className="BookingMessages-Background flex flex-col p-0.5 mt-[calc(var(--panelPadding)*2)] bg-page-background-secondary rounded-toggle w-full">
          <div className="BookingMessages-Container flex-[1_1_auto] self-stretch flex flex-col relative min-h-[40px]">
            {messages && messages.length === 0 && selectedBookingV4 && (
              <span className="NoRecords self-center absolute top-1/2 -translate-y-1/2">
                No messages found.
              </span>
            )}
            <div
              className="BookingMessages-List overflow-auto relative flex-grow"
              ref={scrollRef}
            >
              {loading && <Loading />}
              {messages?.map((message, i) => (
                <MessageRow
                  onClick={handleOnMessageRowClick}
                  key={`selectedMessage - ${i}`}
                  message={message}
                  notifications={selectedBookingV4?.notifications}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectedBookingMessages;
